import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const home = {
    tabs: {
        home: 'Home',
        homeAccessibility: 'Home',
        transactions: 'Transactions',
        analysis: 'Analysis',
        cashFlow: 'Cash flow',
        more: 'More',
    },
    balance: {
        monthResult: 'Month result',
        projectedAvailable: 'Projected available',
        available: 'Available',
        projected: 'Projected balance',
        historical: 'End-of-month balance',
        historicalAvailable: 'End-of-month available',
        historicalPeriod: 'End-of-period balance',
        historicalAvailablePeriod: 'End-of-period available',
        total: 'Total balance',
        hidden: 'R$ ••••',
        allExcluded: 'All accounts are excluded from the total balance',
        seeAccounts: 'See accounts',
        loading: 'Loading balance',
    },
    syncStalled: {
        a11y: 'Sync between devices has stopped. Tap to see why in Backup.',
    },
    firstSteps: {
        title: "Let's get started",
        titleOneLeft: 'Just one step left',
        progress: '{{done}} of {{total}} done',
        createAccount: 'Create bank account',
        addCard: 'Track your credit card',
        addTransaction: 'Log your first expense',
        createBudget: 'Create your first budget',
        createGoal: 'Create your first goal',
        enableListener: 'Turn on automatic capture',
        why: {
            account: 'Every account you use goes here. Then the balance in the app becomes the balance in your pocket.',
            transaction: "One of today's expenses is enough. From there Kontto starts showing where your money goes.",
            card: "Each purchase lands on the right statement and you see how much of the month is already committed before it arrives.",
            budget: 'You set how much you can spend on the category that weighs the most and Kontto warns you before it blows up.',
            goal: 'With every deposit you see how much is left. Saving with a purpose is what makes money last.',
            listener: 'Kontto reads your bank purchase notification and logs the expense on its own. You just confirm.',
        },
        action: {
            account: 'Create account',
            transaction: 'Add expense',
            card: 'Add card',
            budget: 'Create budget',
            goal: 'Create goal',
            listener: 'Turn on capture',
        },
        skipCard: "I don't use a credit card",
        skipListener: "I don't want automatic capture",
        skipBudget: "I don't want a budget right now",
        skipGoal: "I don't have a goal right now",
        dismiss: 'Hide',
        skip: 'Skip',
    },
    activation: {
        title: 'Log your first expense',
        subtitle: 'Start with something you spent today. It takes 10 seconds and your Kontto comes to life.',
        button: 'Add expense',
    },
    // Reminders card (Android, after the 1st expense) — replaced the cold-launch native prompt.
    secondEntry: {
        title: "Don't stop at your first expense",
        subtitle: "Log today's and your money starts getting organized.",
        action: 'Log expense',
        later: 'Not now',
    },
    listenerReinvite: {
        title_one: "You've logged {{count}} entry",
        title_other: "You've logged {{count}} entries",
        subtitle: 'The next ones can come in on their own, from your bank notifications.',
        enable: 'Turn on',
        later: 'Not now',
    },
    reminders: {
        title: 'Never miss a bill',
        subtitle: "We remind you on each bill's due date — no late fees from forgetting. Adjust or turn it off in Settings › Reminders.",
        enable: 'Turn on reminders',
        later: 'Not now',
        grantedToast: 'Reminders on',
        deniedToast: 'No problem — you can turn them on later in Settings › Reminders.',
    },
    proUpsell: {
        backupTitle: 'Lose your phone, lose your history?',
        backupSubtitleCount: 'Protect your {{total}} transactions',
        backupSubtitleDefault: 'Turn on automatic, encrypted backup',
        backupCta: 'Turn on',
        syncTitle: 'Use Kontto on your phone and tablet',
        syncSubtitle: 'End-to-end encrypted sync',
        syncCta: 'Turn on',
        bellTitle: 'Netflix, Spotify and co. charging tomorrow?',
        bellSubtitle: 'We warn you 1 day ahead so you can cancel in time',
        bellCta: 'Turn on',
        trialSuffix: '{{subtitle}} · {{label}} free',
        accessibility: '{{title}}. {{subtitle}}',
        dismiss: 'Dismiss',
    },
    proConversion: {
        messageNeverTrial: 'Meet Kontto Pro · {{label}} free',
        messageNever: 'Meet Kontto Pro',
        messageChurned: 'Come back to Kontto Pro',
        messageTrialed: 'Enjoyed the trial? Go Pro',
        accessibilityHint: 'Open Kontto Pro plans',
    },
    billingIssue: {
        title: "We couldn't charge your Kontto Pro",
        body: 'Update your payment method on the {{store}} to keep Pro.',
        action: 'Update payment',
        accessibilityHint: 'Open your subscription on the {{store}}',
    },
    setupToast: {
        title: 'All set! 🎉',
        message: 'Kontto is all set up — just start using it.',
    },
    flow: {
        income: 'Income',
        expenses: 'Expenses',
    },
    insights: {
        sectionTitle: 'Tips this month',
        empty: 'No tips yet. Log a few expenses to see tips here.',
    },
    shortcuts: {
        sectionTitle: 'Shortcuts',
        items: {
            accounts: 'Accounts',
            cards: 'Cards',
            categories: 'Categories',
            budget: 'Budget',
            goals: 'Goals',
            subscriptions: 'Recurring',
            financings: 'Financing',
            loans: 'Loans',
            personalLoans: 'Owed to me',
            projections: 'Projections',
            reports: 'Reports',
            cashFlow: 'Cash flow',
            advancedReports: 'Adv. reports',
            ai: 'AI assistant',
            receipts: 'Import receipt',
        },
    },
    pending: {
        sectionTitle: 'Pending',
        pay: 'Pay',
        receive: 'Receive',
        invoices: 'Statements',
        transfers: 'Scheduled transfers',
    },
    accounts: {
        sectionTitle: 'Accounts',
        addAccount: 'Add account',
        available: 'Available',
        total: 'Total',
        reserved: 'Reserved',
        projected: 'Projected',
        unassignedInvoice_one: 'Bill with no paying account',
        unassignedInvoice_other: 'Bills with no paying account',
        unassignedInvoiceCards_one: '{{count}} card',
        unassignedInvoiceCards_other: '{{count}} cards',
        unassignedInvoiceA11y_one: 'Bill with no paying account, {{detail}}, {{amount}}. Choose the paying account',
        unassignedInvoiceA11y_other: 'Bills with no paying account, {{detail}}, {{amount}}. Choose the paying account',
    },
    cards: {
        sectionTitle: 'Cards',
        addCard: 'Add card',
        closing: 'Closing',
        total: 'Total',
        upcoming: 'Next',
    },
    installments: {
        sectionTitle: 'Installments',
        perMonth: 'Per month',
        hiddenAmount: '••••',
        incomeShare: '{{share}} of income',
        purchases_one: '{{count}} purchase',
        purchases_other: '{{count}} purchases',
        inSources_one: 'on {{count}} card or account',
        inSources_other: 'on {{count}} cards/accounts',
        reliefTitle: '{{name}} ends',
        reliefSubtitle: 'next relief · {{month}}',
        freeTitle: 'Installment-free in {{month}}',
        freeSubtitle: 'last installment: {{name}}',
        emptyTitle: 'No open installments',
        emptySubtitle: 'Simulate before splitting the next one',
        emptyAccessibility: 'Simulate an installment purchase',
        cardAccessibility: 'Open Installments',
    },
    receivables: {
        sectionTitle: 'Who owes me',
        hiddenAmount: '••••',
        rowAccessibility: 'Who owes me: {{name}}',
        overdueDays_one: '{{count}} day overdue',
        overdueDays_other: '{{count}} days overdue',
        agreedFor: 'Agreed for {{date}}',
        whenPossible: 'Whenever',
        loanCount_one: '{{count}} amount to receive',
        loanCount_other: '{{count}} amounts to receive',
        restPeople_one: '{{count}} more person',
        restPeople_other: '{{count}} more people',
        restAccessibility: 'See all loans',
        restOverdue_one: '{{count}} overdue',
        restOverdue_other: '{{count}} overdue',
        restScheduled_one: '{{count}} with a date',
        restScheduled_other: '{{count}} with a date',
        restWhenPossible: '{{count}} whenever',
        footnotePending: '{{amount}} already counted in {{month}} pending',
        footnotePendingPeriod: '{{amount}} already counted in this period’s pending',
        footnoteInBalance: 'Already added to your balance',
        emptyTitle: 'Lent money to someone?',
        emptySubtitle: 'Track it here and see who has paid you back',
        createAccessibility: 'Record a loan to someone',
    },
    recent: {
        sectionTitle: 'Recent expenses',
        installmentBadge: '{{total}}x',
    },
    budget: {
        sectionTitle: 'Budget',
        empty: 'No budget set. Tap to create.',
    },
    incomeGoals: {
        sectionTitle: 'Income goals',
        empty: 'No income goal. Tap to create.',
        progress: '{{received}} of {{target}}',
        percent: '{{pct}}%',
        received: 'Received',
        target: 'Goal',
    },
    goals: {
        sectionTitle: 'Goals',
        saved: 'Saved',
        target: 'Goal',
        empty: 'No goals yet. Tap to create.',
    },
    reservas: {
        sectionTitle: 'Pockets',
        rowAccessibility: 'Pocket {{name}}',
        accountFallback: 'Account',
        percentSuffix: ' · {{pct}}%',
        noTargetSuffix: ' · no goal',
        hiddenAmount: '••••',
        ofTarget: 'of {{amount}}',
        saved: 'saved',
        createAccessibility: 'Create pocket',
        emptyTitle: 'Create a pocket',
        emptySubtitle: 'Set aside money inside an account without touching the balance.',
    },
    subscriptions: {
        sectionTitle: 'Recurring',
        monthlySpending: 'Monthly spending',
        noneActive: 'No active subscriptions',
        activeCount_one: '{{count}} active subscription',
        activeCount_other: '{{count}} active subscriptions',
    },
    manageHome: 'Manage home screen',
    analysis: {
        title: 'Analysis',
        viewModes: {
            commitments: 'This month',
            new: 'New expenses',
        },
        overview: {
            title: 'Net this month',
            income: 'Income',
            expenses: 'Expenses',
            balance: 'Net',
            expensesSameLevel: 'Expenses in line with last month',
            expensesDelta: 'Expenses {{percent}} vs last month',
        },
        newOverview: {
            title: 'New expenses this month',
            categories: 'Categories',
            average: 'Average',
            topCategory: 'Top category',
            noHighlight: 'No highlight',
            infoAccessibility: 'What counts as new expenses?',
            infoTitle: 'What counts as "New expenses"?',
            infoBody: 'Only spending that ENTERED your financial life this month: one-off purchases made in the period and the first occurrence of a new recurring expense, even if still projected. A new installment plan counts in full, all at once, in the month of the purchase.',
            infoBodyContrast: 'Older recurrences, installments carried over from previous months and financing installments stay out — they remain in the "This month" view. An older purchase added or imported now counts in the month it was made; spending scheduled ahead counts in the month you added it. Use it to answer: "what new spending did I just take on?"',
        },
        categoryRow: {
            collapseSubcategories: 'Collapse subcategories',
            expandSubcategories: 'View subcategories',
            loading: 'Loading…',
            emptySubcategories: 'No transactions to break down.',
            subcategoryShare: '{{percent}}% of the category',
        },
        typeSelector: {
            expenses: 'Expenses',
            income: 'Income',
            accessibility: 'Chart type: {{label}}. Tap to switch',
        },
        breakdown: {
            others: 'Other',
            expenseTitle: 'Expenses by category',
            incomeTitle: 'Income by category',
            newTitle: 'New expenses by category',
            expenseSubtitle: 'A detailed view of your cash flow',
            incomeSubtitle: 'Where your income came from this month',
            newSubtitle: 'Where new expenses started to add up',
            expenseEmptyTitle: 'Nothing relevant to read in this view',
            incomeEmptyTitle: 'No income this month',
            newEmptyTitle: 'No new expenses this month',
            expenseEmptySubtitle: 'When there is income, expenses, or category activity, the category breakdown shows up here.',
            incomeEmptySubtitle: 'When there is income this month — logged or recurring — the category breakdown shows up here.',
            newEmptySubtitle: 'If the spending in this period came from older installments or there have not been new outflows yet, this block stays empty for now.',
            totalSpent: 'Total spent',
            totalReceived: 'Total received',
        },
        empty: {
            title: 'Analysis needs more activity',
            subtitle: 'As soon as there are enough transactions, categories, or history, this screen starts showing trends, composition, and next steps.',
            action: 'Open transactions',
        },
        monthlyFlow: {
            sectionTitle: 'Monthly flow',
            lastMonths: 'Last {{months}} months',
            income: 'Income',
            expenses: 'Expenses',
            emptyTitle: 'Not enough history yet',
            emptySubtitle: 'Once the app builds up a few months of confirmed activity, this chart will show how income and expenses evolve.',
        },
        netWorth: {
            sectionTitle: 'Net worth',
            monthsValue: '{{months}} months',
            totalBalance: 'Total balance',
            emptyTitle: 'No net worth history yet',
            emptySubtitle: 'As accounts and activity are used over time, this curve will show how your total balance evolves.',
        },
        addMovement: 'Add transaction',
        reportsLink: {
            title: 'Advanced reports',
            subtitle: 'Fixed vs variable, comparison, cash flow, and PDF',
        },
        weekday: {
            sectionTitle: 'Spending by weekday',
        },
        fixedVariable: {
            sectionTitle: 'Fixed and variable',
        },
        spendingPace: {
            sectionTitle: 'Spending pace',
            legendCurrent: 'This month',
            legendPrevious: 'Last month',
            sameDay: 'vs same day',
        },
        topExpenses: {
            sectionTitle: 'Biggest expenses',
            noDescription: 'No description',
            empty: 'No expenses this month yet.',
        },
        savingsRate: {
            sectionTitle: 'Savings rate',
            explainer: 'Share of your income left over each month.',
        },
        manageLayout: 'Customize analysis',
    },
    more: {
        title: 'More',
        settingsAccessibility: 'Settings',
        proBadge: 'Pro',
        sections: {
            organize: 'Organize',
            plan: 'Plan',
            track: 'Track',
            analyze: 'Analyze',
            tools: 'Tools',
        },
        items: {
            accounts: 'Accounts',
            creditCards: 'Credit Cards',
            categories: 'Categories',
            tags: 'Tags',
            budget: 'Budget',
            goals: 'Goals and Pockets',
            projections: 'Projections',
            subscriptions: 'Recurring',
            financings: 'Financing',
            loans: 'Loans',
            personalLoans: 'Owed to me',
            installments: 'Installments',
            customReport: 'Custom report',
            advancedReports: 'Advanced reports',
            aiAssistant: 'AI assistant',
            importReceipt: 'Import receipt',
            importCsv: 'Import & export',
        },
    },
} satisfies DeepPartialStrings<TranslationSchema['home']>;
